import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { BookOpenIcon, ChevronDownIcon, PlusIcon, SparklesIcon } from 'lucide-react';

interface NewAutomationSplitButtonProps {
  disabled: boolean;
  /**
   * Why both halves are disabled. Shown by the wrapper: a disabled Tessra Button takes no
   * pointer events, so a `title` on the button itself never appears.
   */
  disabledReason?: string;
  onBlank: () => void;
  /** Absent where no AI model can draft a rule. */
  onDescribe?: () => void;
  onRecipes: () => void;
}

/**
 * Tessra's ButtonGroup only comes in outlined and ghost skins, so the primary
 * split is two primary Buttons joined by their trimmed corners.
 */
export function NewAutomationSplitButton({
  disabled,
  disabledReason,
  onBlank,
  onDescribe,
  onRecipes,
}: NewAutomationSplitButtonProps) {
  const { t } = useTranslation('trackers');
  const moreWays = t('trackersBoard.automationsPanel.moreWaysToCreate');

  return (
    <div className="flex" title={disabledReason}>
      <Button
        type="button"
        size="sm"
        onClick={onBlank}
        disabled={disabled}
        className="rounded-r-none"
      >
        <PlusIcon aria-hidden />
        {t('trackersBoard.automationsPanel.newAutomation')}
      </Button>
      <TooltipProvider>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon-sm"
                  aria-label={moreWays}
                  disabled={disabled}
                  className="rounded-l-none border-l border-primary-foreground/30"
                >
                  <ChevronDownIcon aria-hidden />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{moreWays}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={onBlank}>
              <PlusIcon aria-hidden />
              {t('trackersBoard.automationsPanel.blankRule')}
            </DropdownMenuItem>
            {onDescribe ? (
              <DropdownMenuItem onSelect={onDescribe}>
                <SparklesIcon aria-hidden />
                {t('trackersBoard.automationAi.describe.open')}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={onRecipes}>
              <BookOpenIcon aria-hidden />
              {t('trackersBoard.automationsPanel.fromRecipe')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>
    </div>
  );
}
